<template>
  <main>
    <div class="container super-narrow">
      <div class="main-content-box">
        <div class="top-page-divider">
          <p class="text-center mb-0">
            Already signed up?
            <router-link to="/login">Log in here</router-link>
          </p>
        </div>

        <div v-if="referralCode" class="success-message mb-3 text-center">
          <i class="fas fa-user-friends mr-1"></i>You were referred by a friend!
        </div>

        <h3 class="center h5 text-center mb-4 font-weight-normal">
          Create an Account
        </h3>
        <p class="text-center mx-auto">
          After creating your account, you will use those same credentials to
          log into the game.
        </p>

        <p v-if="awaitingConfirmation" class="success-message m-auto mb-4">
          <i class="fas fa-check-circle mr-1"></i>We sent you a confirmation
          email, please click on the link in the email to finalise your account
          creation!
        </p>
        <div v-if="errors.details" class="error-message mb-4">
          <p>{{ errors.details }}</p>
        </div>
        <form @submit.prevent="executeCaptcha">
          <div class="mb-3">
            <label for="registerUsername" class="form-label sr-only"
              >Username</label
            >
            <input
              type="text"
              v-model="username"
              class="form-control mb-0 p-3"
              placeholder="Username"
              id="registerUsername"
              required
            />
            <template v-if="errors.username">
              <small
                v-for="error in errors.username"
                :key="error"
                class="text-danger"
                >{{ error }}</small
              >
            </template>
          </div>
          <div class="mb-3">
            <label for="registerEmail" class="form-label sr-only"
              >Email Address</label
            >
            <input
              type="email"
              v-model="email"
              class="form-control mb-0 p-3"
              placeholder="Email Address"
              id="registerEmail"
              required
            />
            <template v-if="errors.email">
              <small
                v-for="error in errors.email"
                :key="error"
                class="text-danger"
                >{{ error }}</small
              >
            </template>
          </div>
          <div class="mb-3">
            <label for="registerPassword" class="form-label sr-only"
              >Password</label
            >
            <input
              type="password"
              v-model="password"
              class="form-control mb-0 p-3"
              placeholder="Password"
              id="registerPassword"
              aria-describedby="passwordHelpBlock"
              required
            />
            <template v-if="errors.password">
              <small
                v-for="error in errors.password"
                :key="error"
                class="text-danger"
                >{{ error }}</small
              >
            </template>
            <div
              v-if="password.length > 0 && !isPasswordValid"
              id="passwordHelpBlock"
              class="form-text mt-2"
            >
              Your password must:
              <ul class="mb-0 mt-1">
                <li :class="{ 'text-success': password.length >= 6 }">
                  Be at least 6 characters long
                </li>
                <li :class="{ 'text-success': hasLetter }">
                  Contain at least one letter
                </li>
                <li :class="{ 'text-success': hasNumber }">
                  Contain at least one number
                </li>
                <li :class="{ 'text-success': hasSpecial }">
                  Contain at least one special character
                </li>
              </ul>
            </div>
          </div>
          <div class="mb-3">
            <label for="registerConfirmPassword" class="form-label sr-only"
              >Confirm Password</label
            >
            <input
              type="password"
              v-model="passwordConfirmation"
              class="form-control mb-0 p-3"
              placeholder="Confirm Password"
              id="registerConfirmPassword"
              required
            />
            <template v-if="errors.password_confirmation">
              <small
                v-for="error in errors.password_confirmation"
                :key="error"
                class="text-danger"
                >{{ error }}</small
              >
            </template>
          </div>
          <div class="mb-3 form-check">
            <input
              class="form-check-input mr-2"
              type="checkbox"
              value=""
              id="registerTOS"
              required
            />
            <label class="form-check-label" for="registerTOS">
              I accept the
              <router-link to="/terms" class="text-white"
                >Terms & Conditions</router-link
              >,
              <router-link to="/privacy" class="text-white"
                >Privacy Policy</router-link
              >, and
              <router-link to="/rules" class="text-white">Rules.</router-link>
            </label>
          </div>
          <VueHcaptcha
            ref="hcaptcha"
            sitekey="332bd50d-ceed-4901-b253-345b9792af15"
            size="invisible"
            @verify="register"
            class="text-center"
          />
          <button type="submit" class="btn full-width green">
            <div v-if="loading" class="spinner-border spinner-border-sm mr-2" />
            Register
          </button>
        </form>
      </div>
    </div>
  </main>
</template>

<script>
import APIClient from "@/services/APIClient.js";
import VueHcaptcha from "@hcaptcha/vue3-hcaptcha";
export default {
  components: {
    VueHcaptcha,
  },
  created() {
    document.title = "Register - Near-Reality";

    this.client = new APIClient();
    this.referralCode = this.$route.query.ref || null;
  },
  data() {
    return {
      client: null,
      loading: false,
      awaitingConfirmation: false,
      username: "",
      email: "",
      password: "",
      passwordConfirmation: "",
      referralCode: null,
      errors: [],
    };
  },
  computed: {
    hasLetter() {
      return /[a-zA-Z]/.test(this.password);
    },
    hasNumber() {
      return /\d/.test(this.password);
    },
    hasSpecial() {
      return /[!@#$%^&*(),.?":{}|<>]/.test(this.password);
    },
    isPasswordValid() {
      return (
        this.password.length >= 6 &&
        this.hasLetter &&
        this.hasNumber &&
        this.hasSpecial
      );
    },
  },
  methods: {
    executeCaptcha() {
      this.loading = true;
      this.awaitingConfirmation = false;
      this.$refs.hcaptcha.execute();
    },
    register(tokenStr) {
      console.log("catpcha token:", tokenStr);
      this.loading = true;
      this.awaitingConfirmation = false;
      this.errors = [];

      const payload = {
        username: this.username,
        email: this.email,
        password: this.password,
        password_confirmation: this.passwordConfirmation,
        captchaToken: tokenStr,
        referralCode: this.referralCode,
      };

      this.client
        .postUser(payload)
        .then((response) => {
          console.log(response);
          if (response.status === 200) {
            this.awaitingConfirmation = true;
          } else if (response.status === 201) {
            this.$router.push("/welcome");
          }
          this.loading = false;
        })
        .catch((err) => {
          console.log(err);
          if (err.response.status === 429)
            this.errors = {
              details: "You have been rate limited, please try again later.",
            };
          else this.errors = err.response.data;
          this.loading = false;
          this.awaitingConfirmation = false;
        });
    },
  },
};
</script>
